import React from "react";
import ControlKpiTile from "../../cost-control/ControlKpiTile";
import { netDelta, primaryOwner, driverCause, fmtSignedMoney, fmtPct } from "../format";

function Section({ title, children, linkLabel, onClick }) {
  return (
    <div className="rounded-card border border-card-bd bg-card p-5">
      <div className="text-[13px] font-semibold text-text-2">{title}</div>
      <p className="mt-1 text-[14px] leading-relaxed text-text-2">{children}</p>
      {linkLabel && <button type="button" onClick={onClick} className="mt-2 text-[13px] font-semibold text-primary hover:text-primary-hover">{linkLabel} →</button>}
    </div>
  );
}

// Overview: the executive layer — decision statement, KPI row, and a concise
// evidence-linked narrative. All derived from the frozen decomposition.
export default function OverviewTab({ inv, version, drivers = [], ownership = [], unresolved, onGoTo }) {
  const net = netDelta(drivers);
  const top = [...drivers].sort((a, b) => Math.abs(b.deltaCost) - Math.abs(a.deltaCost))[0];
  const owner = primaryOwner(ownership);
  const runRate = version?.impact?.monthlyRunRateDelta;
  const vsBaseline = version?.impact?.forecast?.p90DeltaVsBaseline;
  const cause = top ? driverCause(top) : "—";
  const needsReview = !!unresolved;

  const tiles = [
    { key: "change", label: "Period change", value: fmtSignedMoney(net), tone: "neutral", href: "#" },
    { key: "rr", label: "Run-rate impact", value: runRate != null ? `${fmtSignedMoney(runRate)}/mo` : "—", tone: net > 0 ? "attention" : "neutral", href: "#" },
    { key: "vb", label: "Forecast vs baseline", value: vsBaseline != null ? fmtSignedMoney(vsBaseline) : "—", tone: "neutral", href: "#" },
    { key: "own", label: "Assigned owner", value: owner ? owner.label : "—", why: owner ? `${fmtSignedMoney(owner.deltaCost)}` : "", tone: "neutral", href: "#" },
    { key: "un", label: "Unresolved", value: unresolved ? fmtSignedMoney(unresolved.deltaCost) : "€0", tone: unresolved ? "attention" : "ok", href: "#" },
  ];

  return (
    <>
      <div className="rounded-card border border-card-bd bg-card px-5 py-4">
        <div className={`mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${needsReview ? "text-warning" : "text-text-2"}`}>
          {needsReview && <span className="inline-block rounded-full bg-warning" style={{ width: 8, height: 8 }} />}
          {needsReview ? "Needs review" : "Decision statement"}
        </div>
        <p className="text-[16px] font-semibold leading-snug text-text">
          {top ? `${top.label || top.key} drove a ${fmtSignedMoney(net)} spend change.` : "No material change detected."}
        </p>
        {owner && (
          <p className="mt-1 text-[14px] leading-relaxed text-text-2">
            {owner.label} accounts for {fmtSignedMoney(owner.deltaCost)} of the change.
            {runRate != null && ` The current run rate adds ${fmtSignedMoney(runRate)} per month.`}
            {unresolved && ` Ownership confidence is limited — ${fmtSignedMoney(unresolved.deltaCost)} remains assigned to ${unresolved.label}.`}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {tiles.map((t) => <ControlKpiTile key={t.key} tile={t} />)}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="What changed" linkLabel="View evidence" onClick={() => onGoTo("evidence")}>
          Net spend changed by {fmtSignedMoney(net)} versus the preceding period.
        </Section>
        <Section title="Root cause" linkLabel="View driver evidence" onClick={() => onGoTo("drivers")}>
          {top ? `${top.label || top.key} accounted for ${fmtPct(top.sharePct)} of the change (${cause.toLowerCase()}).` : "No dominant driver."}
        </Section>
        <Section title="Ownership and impact" linkLabel="View ownership" onClick={() => onGoTo("ownership")}>
          {owner ? `${owner.label} owns ${fmtSignedMoney(owner.deltaCost)} of the change.` : "Ownership not yet resolved."}
          {runRate != null && ` The revised run rate adds ${fmtSignedMoney(runRate)} per month.`}
        </Section>
        <Section title="Recommended response" linkLabel="Open resolution" onClick={() => onGoTo("resolution")}>
          Confirm whether the change was approved and expected, then assign the recurring cost and set a monthly threshold.
        </Section>
      </div>
    </>
  );
}
